import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { Menubar } from 'primereact/menubar';
import { depositsApi, customersApi } from '../../api';
import { CustomerDeposit, Customer } from '../../types';
import { formatCurrency, formatDate } from '../../utils';
import { useDataTableState, useAccountMenuItem } from '../../hooks';

type DepositFilter = 'all' | 'missing_customer';

const FILTER_OPTIONS = [
  { label: 'All Deposits', value: 'all' },
  { label: 'Missing Customer', value: 'missing_customer' },
];

const FILTER_STORAGE_KEY = 'customerIdentification:reviewDeposits:filter';

interface ReviewDepositsTabProps {
  clientId: string;
}

export const ReviewDepositsTab: React.FC<ReviewDepositsTabProps> = ({ clientId }) => {
  const toast = useRef<Toast>(null);
  const [allDeposits, setAllDeposits] = useState<CustomerDeposit[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { accountMenuItem, selectedAccountId } = useAccountMenuItem(clientId, { showAllOption: true });

  // Filter state with localStorage persistence
  const [activeFilter, setActiveFilter] = useState<DepositFilter>(() => {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    return (stored as DepositFilter) || 'missing_customer';
  });
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<CustomerDeposit | null>(null);
  const [customerInput, setCustomerInput] = useState<Customer | string | null>(null);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [isAssigning, setIsAssigning] = useState(false);

  // Persistent sort state
  const { sortField, sortOrder, handleSort } = useDataTableState({
    storageKey: 'customerIdentification:reviewDeposits',
    defaultSort: { field: 'deposit_date', order: -1 },
  });

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [depositsData, customersData] = await Promise.all([
        depositsApi.listDeposits(clientId, { accountId: selectedAccountId }),
        customersApi.listCustomers(clientId),
      ]);

      // Only show approved deposits
      setAllDeposits(depositsData.filter((d) => d.status === 'approved'));
      setCustomers(customersData);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to load data',
        life: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [clientId, selectedAccountId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (value: DepositFilter) => {
    setActiveFilter(value);
    localStorage.setItem(FILTER_STORAGE_KEY, value);
  };

  // Compute counts for each filter
  const filterCounts = {
    all: allDeposits.length,
    missing_customer: allDeposits.filter((d) => !d.customer_id).length,
  };

  // Apply filter to deposits
  const deposits = activeFilter === 'missing_customer'
    ? allDeposits.filter((d) => !d.customer_id)
    : allDeposits;

  const handleAssignClick = (deposit: CustomerDeposit) => {
    setSelectedDeposit(deposit);
    setCustomerInput('');
    setShowAssignDialog(true);
  };

  const searchCustomers = (event: AutoCompleteCompleteEvent) => {
    const query = event.query.toLowerCase();
    const filtered = customers.filter((c) =>
      c.name.toLowerCase().includes(query)
    );
    setFilteredCustomers(filtered);
  };

  const handleConfirmAssign = async () => {
    if (!selectedDeposit || !customerInput) return;

    try {
      setIsAssigning(true);

      let customerId: string;

      if (typeof customerInput === 'string') {
        // Create new customer
        const newCustomer = await customersApi.createCustomer(clientId, {
          name: customerInput,
        });
        customerId = newCustomer.id;
        setCustomers([...customers, newCustomer]);
      } else {
        // Use existing customer
        customerId = customerInput.id;
      }

      await depositsApi.assignToCustomer(clientId, selectedDeposit.id, customerId);
      // Update the deposit in place with the new customer info
      setAllDeposits(allDeposits.map((d) =>
        d.id === selectedDeposit.id
          ? { ...d, customer_id: customerId }
          : d
      ));
      setShowAssignDialog(false);
      setCustomerInput(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Deposit assigned to customer',
        life: 3000,
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.detail || 'Failed to assign deposit',
        life: 5000,
      });
    } finally {
      setIsAssigning(false);
    }
  };

  const amountBodyTemplate = (rowData: CustomerDeposit) => {
    return formatCurrency(rowData.amount);
  };

  const actionBodyTemplate = (rowData: CustomerDeposit) => {
    return (
      <Button
        icon="ms-out ms-out-person-add"
        link
        size="small"
        onClick={() => handleAssignClick(rowData)}
        disabled={isAssigning}
      />
    );
  };

  return (
    <>
      <Toast ref={toast} />

      <Menubar
        className="actions-menubar"
        model={[
          accountMenuItem,
          {
            label: `${FILTER_OPTIONS.find((o) => o.value === activeFilter)?.label} (${filterCounts[activeFilter]})`,
            icon: 'ms-out ms-out-filter-alt',
            items: FILTER_OPTIONS.map((option) => ({
              label: `${option.label} (${filterCounts[option.value as DepositFilter]})`,
              command: () => handleFilterChange(option.value as DepositFilter),
            })),
          },
        ]}
      />

      <DataTable
        value={deposits}
        dataKey="id"
        loading={isLoading}
        paginator
        rows={25}
        rowsPerPageOptions={[25, 50, 100]}
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> approved deposits
            </span>
          ),
        }}
        size="small"
        stripedRows
        scrollable
        scrollHeight="calc(100vh - 200px)"
        emptyMessage="No approved deposits"
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={handleSort}
      >
        <Column 
          field="customer_name" 
          header="Customer Name" 
          sortable style={{ width: '40%' }} />
        <Column
          field="deposit_date"
          header="Date"
          sortable
          style={{ width: '20%' }}
          body={(rowData) => formatDate(rowData.deposit_date)}
        />
        <Column 
          field="amount" 
          header="Amount" 
          body={amountBodyTemplate} 
          sortable style={{ width: '30%' }} />
        <Column body={actionBodyTemplate} />
      </DataTable>

      {/* Assign to Customer Dialog */}
      <Dialog
        header="Assign Deposit to Customer"
        visible={showAssignDialog}
        className="customer-assignment-dialog"
        onHide={() => setShowAssignDialog(false)}
        footer={
          <div className="dialog-footer">
            <Button
              label="Cancel"
              onClick={() => setShowAssignDialog(false)}
              severity="secondary"
            />
            <Button
              label="Assign"
              className="p-button-success"
              onClick={handleConfirmAssign}
              loading={isAssigning}
              disabled={!customerInput}
            />
          </div>
        }
      >
        <div>
          <div className="dialog-context-info">
            <div className="context-row">
              <span className="context-label">Name:</span> {selectedDeposit?.customer_name || '—'}
            </div>
            <div className="context-row">
              <span className="context-label">Date:</span> {selectedDeposit?.deposit_date ? formatDate(selectedDeposit.deposit_date) : '—'}
            </div>
            <div className="context-row">
              <span className="context-label">Amount:</span> {selectedDeposit?.amount ? formatCurrency(selectedDeposit.amount) : '—'}
            </div>
          </div>

          <div className="customer-assignment-form">
            <div className="customer-assignment-label-row">
              <label className="customer-assignment-label">Customer</label>
              <Button
                label="+ New"
                link
                size="small"
                onClick={() => {
                  if (selectedDeposit?.customer_name) {
                    setCustomerInput(selectedDeposit.customer_name);
                  }
                }}
              />
            </div>
            <AutoComplete
              value={customerInput}
              suggestions={filteredCustomers}
              completeMethod={searchCustomers}
              field="name"
              onChange={(e) => setCustomerInput(e.value)}
              placeholder="Select or type new customer name"
              className="customer-assignment-input"
              dropdown
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

// Backward compatible export
export const UnassignedDepositsTab = ReviewDepositsTab;
